<template>
  <div class="now-playing">
    <label>Currently Playing</label>
    <div v-if="displayNames.length" class="thumbs">
      <div class="item" v-for="name in displayNames" :key="name">
        <img
          v-if="thumbMap[name]"
          :src="thumbMap[name]"
          class="thumb"
          :alt="name"
        />
        <div class="name">{{ name }}</div>
      </div>
    </div>
    <div v-else class="empty-value">—</div>
  </div>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue';
import type { CurrentlyPlayingContent } from '@/helpers/api/metrics';
import useAssetThumbnails from '@/composables/useAssetThumbnails';

const props = defineProps<{ items: CurrentlyPlayingContent[] }>();

const { thumbMap, resolveThumbnails, retainThumbnails } = useAssetThumbnails();

// Defensive narrow: legacy backends sometimes serialize this list with
// strings or use the older `title` field name, so we accept `unknown` and
// normalize. The prop type narrows new data correctly; this is just a safety
// net for real-world payloads that don't match the contract.
interface LegacyPlayingItem {
  contentTitle?: string;
  title?: string;
  contentUrl?: string;
}
const itemDetails = computed<{ name: string; url: string }[]>(() => {
  const list = props.items as unknown;
  if (!Array.isArray(list)) return [];
  return (list as unknown[])
    .map((item) => {
      if (typeof item === 'string') return { name: item, url: '' };
      const obj = (item ?? {}) as LegacyPlayingItem;
      return {
        name: obj.contentTitle || obj.title || '',
        url: obj.contentUrl || '',
      };
    })
    .filter((i) => i.name);
});

const displayNames = computed<string[]>(() => itemDetails.value.map((i) => i.name));

watch(
  itemDetails,
  async (details) => {
    await resolveThumbnails(details);
    retainThumbnails(details.map((d) => d.name));
  },
  { immediate: true }
);
</script>

<style scoped>
.now-playing {
  margin-top: 0.75rem;
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  flex-wrap: wrap;
}

.now-playing label {
  font-size: 0.95rem;
  font-weight: 600;
  color: #374151;
  margin: 0.2rem 0.25rem 0 0;
}

.empty-value {
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas,
    'Liberation Mono', 'Courier New', monospace;
}

.thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
}

.item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.6rem 0.8rem;
}

.thumb {
  width: 256px;
  height: 144px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #e5e7eb;
}

.name {
  font-size: 0.9rem;
  color: #374151;
  text-align: center;
  line-height: 1.25;
  max-width: 256px;
  width: 256px;
  display: block;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
